import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState("login"); // 'login', 'reset'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(location.state?.message || "");
  const [error, setError] = useState("");

  // Don't auto-redirect on session - let App.js and useAuth handle authorization checks
  // This ensures users not in ALLOWED_USERS see the unauthorized page, not the app
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        console.log("[Login] Session detected, redirecting to home to trigger auth checks");
        // Redirect to "/" which will trigger useAuth to check ALLOWED_USERS
        navigate("/");
      }
    };
    checkSession();
  }, [navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Login error:", error);
        setError(error.message);
        setLoading(false);
      } else if (data?.session) {
        console.log("Login successful, redirecting...");
        // Success - redirect to home
        navigate("/");
      } else {
        console.error("Login failed: No session returned");
        setError("Login mislukt. Probeer opnieuw.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Unexpected error during login:", err);
      setError("Er is een onverwachte fout opgetreden.");
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/reset-password",
    });

    if (error) {
      setError(error.message);
    } else {
      setMessage("Wachtwoord reset link verzonden naar je email.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md border border-gray-200">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-800 mb-1">
              Bonamuze
            </h1>
            <div className="text-sm text-gray-500">Esthetisch beheerportaal</div>
          </div>
          <p className="text-gray-600 text-sm font-medium">
            {mode === "login" && "Log in op je account"}
            {mode === "reset" && "Reset je wachtwoord"}
          </p>
        </div>

        {/* Messages */}
        {message && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
            {message}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Email/Password Form */}
        <form onSubmit={mode === "login" ? handleEmailLogin : handlePasswordReset}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="jouw@email.com"
              required
            />
          </div>

          {mode !== "reset" && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Wachtwoord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>
          )}

          {mode === "login" && (
            <div className="mb-6 text-right">
              <button
                type="button"
                onClick={() => setMode("reset")}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Wachtwoord vergeten?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Bezig..." : mode === "login" ? "Inloggen" : "Reset link verzenden"}
          </button>
        </form>

        {/* Info and navigation */}
        <div className="mt-4 text-center text-sm">
          {mode === "login" && (
            <p className="text-gray-500">
              Registratie alleen mogelijk via uitnodiging
            </p>
          )}
          {mode === "reset" && (
            <p className="text-gray-600">
              Terug naar{" "}
              <button
                onClick={() => {
                  setMode("login");
                  setError("");
                  setMessage("");
                }}
                className="text-blue-600 hover:text-blue-700 font-semibold"
              >
                inloggen
              </button>
            </p>
          )}
        </div>

      </div>
    </div>
  );
}
